import { useState, useCallback, useEffect, useRef } from "react";
import {
  useSettings,
  DEFAULT_SHOP_SETTINGS,
  type ShopSettings,
  type ShopPlacementType,
} from "../hooks/useSettings";
import { setAdminApiKey, getAdminApiKey } from "../utils/adminKey";
import { useToast } from "../hooks/useToast";

// ─── Option lists ─────────────────────────────────────────────────────────────
const PLACEMENT_TYPES: { label: string; value: ShopPlacementType; desc: string }[] = [
  { label: "Floor", value: "floor", desc: "Products placed on flat surfaces (furniture, decor, shoes)" },
  { label: "Wall", value: "wall", desc: "Products mounted on walls (art, mirrors, frames)" },
  { label: "Face", value: "face_accessory", desc: "Accessories worn on the face (glasses, jewellery)" },
  { label: "Hand", value: "hand", desc: "Accessories worn on hands (rings, bracelets, watches)" },
  { label: "Foot", value: "foot", desc: "Footwear (shoes, sneakers)" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Server returns stored admin keys as SHA-256 hex digests; raw keys never match this.
const isSha256Hex = (s: string) => /^[0-9a-f]{64}$/.test(s);

function toComparableSnapshot(s: ShopSettings): string {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { shopify_api_token: _, ...rest } = s;
  return JSON.stringify(rest);
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function SectionHeader({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="border-b border-slate-100 pb-4">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <p className="mt-0.5 text-sm text-slate-500">{desc}</p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function SettingsPage() {
  const { settings, loading, saving, saveSettings } = useSettings();
  const { showError, showSuccess } = useToast();

  const [form, setForm] = useState<ShopSettings>(DEFAULT_SHOP_SETTINGS);
  const [shopifyTokenInput, setShopifyTokenInput] = useState("");
  const [clearStoredToken, setClearStoredToken] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState("");

  const savedSnapshotRef = useRef<string>(toComparableSnapshot(DEFAULT_SHOP_SETTINGS));
  const initializedRef = useRef(false);

  useEffect(() => {
    if (settings && !initializedRef.current) {
      initializedRef.current = true;
      setForm(settings);
      savedSnapshotRef.current = toComparableSnapshot(settings);
      if (settings.admin_api_keys.length > 0) {
        // Server returns digests, never raw keys — do NOT write them to
        // localStorage (the API client must keep sending the raw key).
        // If there's already an admin key stored, unlock the section
        setAdminUnlocked(true);
      } else if (!settings.admin_api_keys.length && getAdminApiKey()) {
        setAdminApiKey(null);
      }
    }
  }, [settings]);

  const isDirty =
    toComparableSnapshot(form) !== savedSnapshotRef.current ||
    shopifyTokenInput.trim().length > 0 ||
    clearStoredToken;

  const setField = useCallback(<K extends keyof ShopSettings>(key: K, value: ShopSettings[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAddAdminKey = useCallback(() => {
    const trimmed = adminKeyInput.trim();
    if (!trimmed) return;
    setForm((prev) => ({ ...prev, admin_api_keys: [...prev.admin_api_keys.filter(k => k), trimmed] }));
    setAdminKeyInput("");
  }, [adminKeyInput]);

  const handleRemoveAdminKey = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      admin_api_keys: prev.admin_api_keys.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const payload: Partial<ShopSettings> = {
      ...form,
      admin_api_keys: form.admin_api_keys.filter((k) => k.trim().length > 0),
    };

    if (clearStoredToken) {
      payload.shopify_api_token = '';
    } else if (shopifyTokenInput.trim().length > 0) {
      payload.shopify_api_token = shopifyTokenInput.trim();
    }

    try {
      const saved = await saveSettings(payload);
      savedSnapshotRef.current = toComparableSnapshot(saved);
      setForm(saved);
      setShopifyTokenInput("");
      setClearStoredToken(false);
      // saved.admin_api_keys contains server-side digests — never store those.
      // Only persist a key the user actually typed this session (raw, non-digest).
      const submittedKeys = payload.admin_api_keys ?? [];
      const newRawKey = submittedKeys.find((k) => !isSha256Hex(k));
      if (newRawKey) {
        setAdminApiKey(newRawKey);
      } else if (submittedKeys.length === 0) {
        setAdminApiKey(null);
      }
      showSuccess("Settings saved.");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save settings.";
      showError(msg);
    }
  }, [form, shopifyTokenInput, clearStoredToken, saveSettings, showSuccess, showError]);

  const handleReset = useCallback(() => {
    if (settings) {
      setForm(settings);
      savedSnapshotRef.current = toComparableSnapshot(settings);
    }
    setShopifyTokenInput("");
    setClearStoredToken(false);
  }, [settings]);

  const disabled = loading || saving;

  return (
    <div className="page-grid">
      {/* ── Header ── */}
      <div className="glass-panel">
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Control how AR models are generated and displayed in your store.
        </p>
      </div>

      {/* ── Save bar ── */}
      <div className="glass-panel flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          {loading ? (
            <span className="text-slate-400">Loading…</span>
          ) : isDirty ? (
            <span className="font-semibold text-amber-600">You have unsaved changes.</span>
          ) : (
            <span className="text-slate-500">All changes saved.</span>
          )}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={disabled || !isDirty}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !isDirty}
            className="rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* ── Section 1: AR Placement ── */}
      <div className="glass-panel space-y-5">
        <SectionHeader
          title="Default AR placement"
          desc="Choose where AR models are placed when shoppers open your products in AR. This applies to all products unless you override it per-product."
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PLACEMENT_TYPES.map((p) => (
            <button
              key={p.value}
              type="button"
              disabled={disabled}
              onClick={() => setField("default_placement_type", p.value)}
              className={[
                "rounded-2xl border-2 p-4 text-left transition",
                form.default_placement_type === p.value
                  ? "border-indigo-400 bg-indigo-50"
                  : "border-slate-200 bg-white hover:border-slate-300",
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              <p className={`text-sm font-semibold ${form.default_placement_type === p.value ? "text-indigo-700" : "text-slate-800"}`}>
                {p.label}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── Section 2: Generation mode ── */}
      <div className="glass-panel space-y-5">
        <SectionHeader
          title="Default generation mode"
          desc="Set how products are captured by default. You can change this per product when generating."
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setField("default_capture_mode", "quick_capture")}
            className={[
              "rounded-2xl border-2 p-4 text-left transition",
              form.default_capture_mode === "quick_capture"
                ? "border-indigo-400 bg-indigo-50"
                : "border-slate-200 bg-white hover:border-slate-300",
              disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            <p className={`text-sm font-semibold ${form.default_capture_mode === "quick_capture" ? "text-indigo-700" : "text-slate-800"}`}>
              Single Reference
            </p>
            <p className="mt-0.5 text-xs text-slate-500">Use your main product photo — fastest way to get an AR model live</p>
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setField("default_capture_mode", "multi_angle_capture")}
            className={[
              "rounded-2xl border-2 p-4 text-left transition",
              form.default_capture_mode === "multi_angle_capture"
                ? "border-indigo-400 bg-indigo-50"
                : "border-slate-200 bg-white hover:border-slate-300",
              disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            <p className={`text-sm font-semibold ${form.default_capture_mode === "multi_angle_capture" ? "text-indigo-700" : "text-slate-800"}`}>
              Multi-View
            </p>
            <p className="mt-0.5 text-xs text-slate-500">4 photos (front, left, back, right) — better 3D shape accuracy</p>
          </button>
        </div>
      </div>

      {/* ── Section 3: Storefront experience ── */}
      <div className="glass-panel space-y-5">
        <SectionHeader
          title="Storefront experience"
          desc="Customise how the AR button and 3D viewer appear on your product pages."
        />
        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-700">
            AR button label
            <input
              type="text"
              value={form.ar_button_label}
              onChange={(e) => setField("ar_button_label", e.target.value)}
              disabled={disabled}
              maxLength={100}
              placeholder="View in your space"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
            />
            <span className="mt-1 block text-xs text-slate-400">The text shown on the AR button in your storefront.</span>
          </label>

          <label className="flex cursor-pointer items-start gap-3">
            <div className="relative mt-0.5 flex-shrink-0">
              <input
                type="checkbox"
                checked={form.enable_desktop_3d}
                onChange={(e) => setField("enable_desktop_3d", e.target.checked)}
                disabled={disabled}
                className="sr-only"
              />
              <div
                onClick={() => !disabled && setField("enable_desktop_3d", !form.enable_desktop_3d)}
                className={`h-6 w-11 rounded-full transition-colors ${form.enable_desktop_3d ? "bg-indigo-500" : "bg-slate-200"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${form.enable_desktop_3d ? "translate-x-5" : "translate-x-0.5"}`}
                />
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">Show 3D viewer on desktop</p>
              <p className="text-xs text-slate-400">Customers on desktop can spin and inspect the 3D model (AR itself is mobile-only).</p>
            </div>
          </label>
        </div>
      </div>

      {/* ── Section 4: Admin access ── */}
      <div className="glass-panel space-y-5">
        <SectionHeader
          title="Admin API key"
          desc="Internal testing credential. If our team has issued you an Admin API key, enter it here to unlock unlimited generation and advanced features. Not required for normal use."
        />

        {!adminUnlocked ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="password"
                value={adminKeyInput}
                onChange={(e) => setAdminKeyInput(e.target.value)}
                disabled={disabled}
                placeholder="Paste your Admin API key"
                className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && adminKeyInput.trim()) {
                    setForm((prev) => ({ ...prev, admin_api_keys: [adminKeyInput.trim()] }));
                    setAdminKeyInput("");
                    setAdminUnlocked(true);
                  }
                }}
              />
              <button
                type="button"
                disabled={disabled || !adminKeyInput.trim()}
                onClick={() => {
                  handleAddAdminKey();
                  setAdminUnlocked(true);
                }}
                className="rounded-2xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                Unlock
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Don&apos;t have a key? You don&apos;t need one — your plan quota covers normal use.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <svg className="h-4 w-4 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-700">
                Admin access active — {form.admin_api_keys.filter(k => k).length} key{form.admin_api_keys.filter(k => k).length !== 1 ? 's' : ''} stored
              </p>
              <button
                type="button"
                onClick={() => setAdminUnlocked(false)}
                className="ml-auto text-xs text-slate-400 hover:text-slate-600"
              >
                Hide
              </button>
            </div>

            {/* Stored keys */}
            <div className="space-y-2">
              {form.admin_api_keys.filter(k => k).map((key, index) => (
                <div key={`admin-key-${index}`} className="flex gap-2">
                  <input
                    type="password"
                    value={key}
                    readOnly
                    className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveAdminKey(index)}
                    disabled={disabled}
                    className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="password"
                  value={adminKeyInput}
                  onChange={(e) => setAdminKeyInput(e.target.value)}
                  disabled={disabled}
                  placeholder="Add another key"
                  className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={handleAddAdminKey}
                  disabled={disabled || !adminKeyInput.trim()}
                  className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Advanced settings — only visible when unlocked */}
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Advanced settings</p>

              <label className="block text-sm font-semibold text-slate-700">
                Dimension unit
                <select
                  value={form.default_dimension_unit}
                  onChange={(e) => setField("default_dimension_unit", e.target.value as any)}
                  disabled={disabled}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                >
                  <option value="cm">Centimetres (cm)</option>
                  <option value="mm">Millimetres (mm)</option>
                  <option value="in">Inches (in)</option>
                </select>
                <span className="mt-1 block text-xs text-slate-400">Units used for product dimension inputs.</span>
              </label>

              <label className="block text-sm font-semibold text-slate-700">
                Dimension strategy
                <select
                  value={form.default_dimension_strategy}
                  onChange={(e) => setField("default_dimension_strategy", e.target.value as any)}
                  disabled={disabled}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                >
                  <option value="manual_optional">Manual – optional</option>
                  <option value="manual_required">Manual – required before generation</option>
                  <option value="assume_1m">Auto-scale to 1 m</option>
                </select>
              </label>

              <label className="block text-sm font-semibold text-slate-700">
                Publishing mode
                <select
                  value={form.publishing_mode}
                  onChange={(e) => setField("publishing_mode", e.target.value as any)}
                  disabled={disabled}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                >
                  <option value="shopify_media">Shopify Media (recommended)</option>
                  <option value="external_r2">Cloudflare R2 only</option>
                  <option value="hybrid">Hybrid (R2 + Shopify media)</option>
                </select>
                <span className="mt-1 block text-xs text-slate-400">Where generated GLB files are stored.</span>
              </label>

              <label className="flex cursor-pointer items-start gap-3">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={form.auto_generate_on_new_products}
                    onChange={(e) => setField("auto_generate_on_new_products", e.target.checked)}
                    disabled={disabled}
                    className="sr-only"
                  />
                  <div
                    onClick={() => !disabled && setField("auto_generate_on_new_products", !form.auto_generate_on_new_products)}
                    className={`h-6 w-11 rounded-full transition-colors ${form.auto_generate_on_new_products ? "bg-indigo-500" : "bg-slate-200"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${form.auto_generate_on_new_products ? "translate-x-5" : "translate-x-0.5"}`}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Auto-generate on new products</p>
                  <p className="text-xs text-slate-400">Automatically queue AR generation when a new product is synced.</p>
                </div>
              </label>

              {/* Shopify recovery token */}
              <div className="space-y-2 border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700">Shopify Admin API recovery token</p>
                <p className="text-xs text-slate-400">
                  Only needed if the OAuth token becomes invalid after reinstalling the app. Get it from Shopify Admin → Settings → Apps → Develop apps.
                </p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={clearStoredToken ? "" : shopifyTokenInput}
                    onChange={(e) => {
                      setClearStoredToken(false);
                      setShopifyTokenInput(e.target.value);
                    }}
                    disabled={disabled || clearStoredToken}
                    placeholder={
                      clearStoredToken
                        ? "Will be cleared on save"
                        : form.shopify_api_token_set
                        ? "••••••••  (stored — paste new to replace)"
                        : "shpat_xxxxxxxxxxxx"
                    }
                    className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                  />
                  {form.shopify_api_token_set && !clearStoredToken && (
                    <button
                      type="button"
                      onClick={() => { setClearStoredToken(true); setShopifyTokenInput(""); }}
                      disabled={disabled}
                      className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-100 disabled:opacity-60"
                    >
                      Clear
                    </button>
                  )}
                  {clearStoredToken && (
                    <button
                      type="button"
                      onClick={() => setClearStoredToken(false)}
                      disabled={disabled}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      Undo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
